import type {
  BatchEncodeItem,
  AssociationMapping,
  EncodeResult,
  EncodingSystem,
  SavedAssociation,
  WikipediaSummary,
} from "../types";
import {
  DEFAULT_ENCODING_SYSTEM,
  getEncodingProfile,
  matchesProfileWikipediaUrl,
} from "../encodingProfiles";

const WIKIPEDIA_PREVIEW_MAX_LENGTH = 130;

export type NumberInputNormalization = {
  duplicateRowsRemoved: number;
  nonNumericCharactersRemoved: number;
  truncated: boolean;
  value: string;
};

export function normalizeNumberInput(
  value: string,
  options: { limit: number; stripNonNumeric: boolean },
): NumberInputNormalization {
  const seen = new Set<string>();
  const normalizedLines: string[] = [];
  let duplicateRowsRemoved = 0;
  let nonNumericCharactersRemoved = 0;

  for (const line of value.split(/\r?\n/)) {
    const normalizedLine = options.stripNonNumeric
      ? line.replace(/[^0-9]/g, "")
      : line;
    nonNumericCharactersRemoved += line.length - normalizedLine.length;
    const rowKey = normalizedLine.trim();
    if (rowKey && seen.has(rowKey)) {
      duplicateRowsRemoved += 1;
      continue;
    }
    if (rowKey) {
      seen.add(rowKey);
    }
    normalizedLines.push(normalizedLine);
  }

  const limited = limitNumberRows(normalizedLines.join("\n"), options.limit);
  return {
    duplicateRowsRemoved,
    nonNumericCharactersRemoved,
    truncated: limited.truncated,
    value: limited.value,
  };
}

export function shouldBlockNumberRowInsertion(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  limit: number,
): boolean {
  const selectedText = value.slice(selectionStart, selectionEnd);
  const currentRows = value.split(/\r?\n/).length;
  const replacedRowBreaks = selectedText.match(/\r?\n/g)?.length ?? 0;
  return currentRows + 1 - replacedRowBreaks > limit;
}

export function parseNumberLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function limitNumberRows(
  value: string,
  limit: number,
): { value: string; truncated: boolean } {
  const lines = value.split(/\r?\n/);
  let numberCount = 0;
  for (const [index, line] of lines.entries()) {
    if (line.trim()) {
      numberCount += 1;
    }
    if (numberCount > limit) {
      return {
        value: lines.slice(0, index).join("\n"),
        truncated: true,
      };
    }
  }
  return { value, truncated: false };
}

export function associationMatchesResult(
  association: SavedAssociation,
  result: EncodeResult,
): boolean {
  const encodingSystem = association.encoding_system ?? "english";
  return (
    encodingSystem === (result.encoding_system ?? "english") &&
    association.digits === result.digits &&
    association.chunks.length === result.chunks.length &&
    association.chunks.every((chunk, index) => chunk === result.chunks[index]) &&
    association.candidates.length === result.candidates.length &&
    association.candidates.every((candidate, index) => {
      const resultCandidate = result.candidates[index];
      return (
        candidate.number === resultCandidate.number &&
        candidate.noun === resultCandidate.noun &&
        candidate.noun_qid === resultCandidate.noun_qid &&
        (encodingSystem === "english" || (
          candidate.pronunciation === resultCandidate.pronunciation &&
          candidate.pronunciation_dialect === resultCandidate.pronunciation_dialect &&
          candidate.pronunciation_variant === resultCandidate.pronunciation_variant
        ))
      );
    })
  );
}

export function savedAssociationAsEncodeItem(
  association: SavedAssociation,
): BatchEncodeItem {
  return associationMappingAsEncodeItem(association);
}

export function associationMappingAsEncodeItem(
  association: AssociationMapping,
  stochastic = false,
): BatchEncodeItem {
  const words = association.candidates.map((candidate) => candidate.noun);
  return {
    digits: association.digits,
    ok: true,
    result: {
      ...(association.encoding_system
        ? { encoding_system: association.encoding_system }
        : {}),
      digits: association.digits,
      chunks: association.chunks,
      words,
      text: words.join(" "),
      stochastic,
      candidates: association.candidates,
    },
  };
}

export function savedAssociationsToCsv(
  associations: SavedAssociation[],
): string {
  const includeEncodingSystem = associations.some(
    (association) => (association.encoding_system ?? "english") !== "english",
  );
  const rows = [
    [
      ...(includeEncodingSystem ? ["encoding_system"] : []),
      "number",
      "mapping",
      "chunks",
      "saved_at",
    ],
    ...associations.map((association) => [
      ...(includeEncodingSystem
        ? [association.encoding_system ?? "english"]
        : []),
      association.digits,
      association.candidates.map((candidate) => candidate.noun).join(" "),
      association.chunks.join(" "),
      association.created_at,
    ]),
  ];
  return `\uFEFF${rows
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n")}\r\n`;
}

function csvCell(value: string): string {
  const safeValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safeValue.replaceAll('"', '""')}"`;
}

export function wikipediaPageUrl(
  word: string,
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): string {
  const title = capitalizeWord(word).replace(/\s+/g, "_");
  return `https://${wikipediaLanguage(encodingSystem)}.wikipedia.org/wiki/${encodeURIComponent(title)}`;
}

export function wikipediaSummaryUrl(
  word: string,
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): string {
  return `https://${wikipediaLanguage(encodingSystem)}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
    capitalizeWord(word),
  )}`;
}

export function wikipediaTitleFromPageUrl(
  value: unknown,
  encodingSystem: EncodingSystem,
): string | null {
  if (
    typeof value !== "string" ||
    !matchesProfileWikipediaUrl(value, encodingSystem)
  ) {
    return null;
  }
  const encodedTitle = new URL(value).pathname.startsWith("/wiki/")
    ? new URL(value).pathname.slice("/wiki/".length)
    : "";
  if (!encodedTitle) {
    return null;
  }
  try {
    const title = decodeURIComponent(encodedTitle).trim();
    return title || null;
  } catch {
    return null;
  }
}

export function wikipediaExtractUrl(
  title: string,
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): string {
  const parameters = new URLSearchParams({
    action: "query",
    format: "json",
    formatversion: "2",
    origin: "*",
    prop: "extracts",
    explaintext: "1",
    exsectionformat: "plain",
    redirects: "1",
    titles: title,
  });
  return `https://${wikipediaLanguage(encodingSystem)}.wikipedia.org/w/api.php?${parameters.toString()}`;
}

export function spanishWikipediaSearchUrl(word: string): string {
  return wikipediaSearchUrl(word, "spanish");
}

export function wikipediaSearchUrl(
  word: string,
  encodingSystem: EncodingSystem,
): string {
  const parameters = new URLSearchParams({
    action: "query",
    exintro: "1",
    explaintext: "1",
    format: "json",
    formatversion: "2",
    generator: "search",
    gsrlimit: "1",
    gsrnamespace: "0",
    gsrsearch: word.trim(),
    inprop: "url",
    origin: "*",
    pithumbsize: "320",
    piprop: "thumbnail",
    prop: "extracts|info|pageimages",
  });
  return `https://${wikipediaLanguage(encodingSystem)}.wikipedia.org/w/api.php?${parameters.toString()}`;
}

export async function fetchWikipediaSummary(
  word: string,
  encodingSystemOrFetcher: EncodingSystem | typeof fetch = DEFAULT_ENCODING_SYSTEM,
  selectedFetcher: typeof fetch = fetch,
): Promise<WikipediaSummary | null> {
  const encodingSystem = typeof encodingSystemOrFetcher === "string"
    ? encodingSystemOrFetcher
    : DEFAULT_ENCODING_SYSTEM;
  const fetcher = typeof encodingSystemOrFetcher === "function"
    ? encodingSystemOrFetcher
    : selectedFetcher;
  return fetchWikipediaSummaryWithPolicy(
    word,
    encodingSystem,
    fetcher,
    getEncodingProfile(encodingSystem).wikipediaSearchFallback,
  );
}

export async function fetchExactWikipediaSummary(
  title: string,
  encodingSystem: EncodingSystem,
  fetcher: typeof fetch = fetch,
): Promise<WikipediaSummary | null> {
  return fetchWikipediaSummaryWithPolicy(
    title,
    encodingSystem,
    fetcher,
    false,
  );
}

async function fetchWikipediaSummaryWithPolicy(
  word: string,
  encodingSystem: EncodingSystem,
  fetcher: typeof fetch,
  searchFallback: boolean,
): Promise<WikipediaSummary | null> {
  let response: Response;
  try {
    response = await fetcher(wikipediaSummaryUrl(word, encodingSystem), {
      headers: { accept: "application/json" },
    });
  } catch {
    return null;
  }
  if (!response.ok) {
    return searchFallback
      ? fetchProfileWikipediaSearch(word, encodingSystem, fetcher)
      : null;
  }
  let summary: WikipediaSummary;
  try {
    const payload = (await response.json()) as Partial<WikipediaSummary> | null;
    if (
      payload === null ||
      typeof payload.title !== "string" ||
      typeof payload.extract !== "string"
    ) {
      return null;
    }
    summary = sanitizeWikipediaSummary(
      payload as WikipediaSummary,
      encodingSystem,
    );
  } catch {
    return null;
  }
  if (
    summary.type !== "disambiguation" &&
    !/\bmay refer to:\s*$/i.test(summary.extract)
  ) {
    return summary;
  }
  try {
    const extractResponse = await fetcher(
      wikipediaExtractUrl(summary.title, encodingSystem),
      {
      headers: { accept: "application/json" },
      },
    );
    if (!extractResponse.ok) {
      return summary;
    }
    const payload = (await extractResponse.json()) as {
      query?: { pages?: Array<{ extract?: string }> };
    };
    const fullExtract = payload.query?.pages?.[0]?.extract?.trim();
    return fullExtract
      ? { ...summary, disambiguation_extract: fullExtract }
      : summary;
  } catch {
    return summary;
  }
}

async function fetchProfileWikipediaSearch(
  word: string,
  encodingSystem: EncodingSystem,
  fetcher: typeof fetch,
): Promise<WikipediaSummary | null> {
  try {
    const response = await fetcher(wikipediaSearchUrl(word, encodingSystem), {
      headers: { accept: "application/json" },
    });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as {
      query?: {
        pages?: Array<{
          extract?: string;
          fullurl?: string;
          thumbnail?: { source?: string };
          title?: string;
        }>;
      };
    };
    const page = payload.query?.pages?.[0];
    const title = page?.title?.trim();
    const extract = page?.extract?.trim();
    if (!page || !title || !extract) {
      return null;
    }
    return sanitizeWikipediaSummary({
      title,
      extract,
      content_urls: {
        desktop: {
          page: validWikipediaPageUrl(page.fullurl, encodingSystem)
            ? page.fullurl
            : wikipediaPageUrl(title, encodingSystem),
        },
      },
      thumbnail: page.thumbnail,
    }, encodingSystem);
  } catch {
    return null;
  }
}

export function wikipediaCacheKey(
  encodingSystem: EncodingSystem,
  word: string,
  wikipediaUrl?: string | null,
): string {
  const exactTitle = wikipediaTitleFromPageUrl(wikipediaUrl, encodingSystem);
  const identity = exactTitle === null
    ? word.trim().toLocaleLowerCase()
    : `wiki:${exactTitle}`;
  return `${encodingSystem}:${identity}`;
}

function wikipediaLanguage(encodingSystem: EncodingSystem): string {
  return getEncodingProfile(encodingSystem).wikipediaLanguage;
}

function sanitizeWikipediaSummary(
  summary: WikipediaSummary,
  encodingSystem: EncodingSystem,
): WikipediaSummary {
  const page = summary.content_urls?.desktop?.page;
  const { thumbnail, ...summaryWithoutThumbnail } = summary;
  const safeSummary = validWikimediaThumbnailUrl(thumbnail?.source)
    ? summary
    : summaryWithoutThumbnail;
  if (page === undefined || validWikipediaPageUrl(page, encodingSystem)) return safeSummary;
  return {
    ...safeSummary,
    content_urls: {
      ...summary.content_urls,
      desktop: {
        ...summary.content_urls?.desktop,
        page: wikipediaPageUrl(summary.title, encodingSystem),
      },
    },
  };
}

function validWikimediaThumbnailUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      (url.origin === "https://upload.wikimedia.org" ||
        url.origin === "https://thumb.wikimedia.org");
  } catch {
    return false;
  }
}

function validWikipediaPageUrl(
  value: unknown,
  encodingSystem: EncodingSystem,
): value is string {
  return typeof value === "string" &&
    matchesProfileWikipediaUrl(value, encodingSystem);
}

export function capitalizeWord(word: string): string {
  const normalized = word.trim();
  if (normalized.length === 0) {
    return normalized;
  }
  return normalized[0].toUpperCase() + normalized.slice(1);
}

export function shortenWikipediaExtract(extract: string): string {
  const normalized = extract.trim();
  if (normalized.length <= WIKIPEDIA_PREVIEW_MAX_LENGTH) {
    return normalized;
  }
  const shortened = normalized.slice(0, WIKIPEDIA_PREVIEW_MAX_LENGTH - 3);
  const wordBoundary = shortened.lastIndexOf(" ");
  return `${shortened.slice(0, wordBoundary > 0 ? wordBoundary : undefined)}...`;
}
